import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfinitePosts } from '../../hooks/usePosts';
import PostCard from './PostCard';
import CreatePost from './CreatePost';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../hooks/useAuth';
import { motion } from 'framer-motion';
import { staggerContainer } from '../../utils/animations';

export default function Feed() {
  const { canCreatePosts } = usePermissions();
  const { user } = useAuth();
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfinitePosts(20);
  
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const postRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Listen for openCreatePost event from Header
  useEffect(() => {
    const handleOpenCreatePost = () => {
      setShowCreatePost(true);
    };

    window.addEventListener('openCreatePost', handleOpenCreatePost);
    return () => window.removeEventListener('openCreatePost', handleOpenCreatePost);
  }, []);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const loadMoreElement = loadMoreRef.current;
    if (!loadMoreElement || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreElement);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Flatten all pages of posts into a single array
  const posts = data?.pages.flat() || [];

  // Handle scrolling to specific post from URL parameter
  useEffect(() => {
    const postId = searchParams.get('post');
    if (postId && posts.length > 0 && !isLoading) {
      const postElement = postRefs.current[postId];
      if (postElement) {
        // Scroll to the post with some offset for better visibility
        setTimeout(() => {
          postElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          // Add a temporary highlight effect
          postElement.style.boxShadow = '0 0 0 3px #3B82F6, 0 0 20px rgba(59, 130, 246, 0.3)';
          postElement.style.transition = 'box-shadow 0.3s ease-in-out';
          
          // Remove highlight after 3 seconds
          setTimeout(() => {
            postElement.style.boxShadow = '';
          }, 3000);
          
          // Clear the query parameter so it doesn't interfere with navigation
          setSearchParams({});
        }, 500); // Wait for render
      }
    }
  }, [posts, searchParams, setSearchParams, isLoading]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {canCreatePosts && !showCreatePost && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <button
              onClick={() => setShowCreatePost(true)}
              className="w-full flex items-center space-x-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 p-3 rounded-lg transition-colors"
            >
              <div className="h-10 w-10 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-white text-sm font-medium">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                )}
              </div>
              <span className="flex-1 text-gray-500 dark:text-gray-400">Start a post...</span>
            </button>
          </div>
        )}
        {showCreatePost && <CreatePost onClose={() => setShowCreatePost(false)} />}
        <div className="theme-card shadow-sm p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading posts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        {canCreatePosts && !showCreatePost && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <button
              onClick={() => setShowCreatePost(true)}
              className="w-full flex items-center space-x-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 p-3 rounded-lg transition-colors"
            >
              <div className="h-10 w-10 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-white text-sm font-medium">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                )}
              </div>
              <span className="flex-1 text-gray-500 dark:text-gray-400">Start a post...</span>
            </button>
          </div>
        )}
        {showCreatePost && <CreatePost onClose={() => setShowCreatePost(false)} />}
        <div className="theme-card shadow-sm p-8 text-center">
          <div className="text-red-500 mb-2">
            <svg className="h-8 w-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">Failed to load posts</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="space-y-4">
        {canCreatePosts && !showCreatePost && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <button
              onClick={() => setShowCreatePost(true)}
              className="w-full flex items-center space-x-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 p-3 rounded-lg transition-colors"
            >
              <div className="h-10 w-10 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-white text-sm font-medium">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                )}
              </div>
              <span className="flex-1 text-gray-500 dark:text-gray-400">Start a post...</span>
            </button>
          </div>
        )}
        {showCreatePost && <CreatePost onClose={() => setShowCreatePost(false)} />}
        <div className="theme-card shadow-sm p-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            No posts yet.{canCreatePosts && ' Be the first to share something!'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-4"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {canCreatePosts && !showCreatePost && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <button
            onClick={() => setShowCreatePost(true)}
            className="w-full flex items-center space-x-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 p-3 rounded-lg transition-colors"
          >
            <div className="h-10 w-10 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="h-10 w-10 rounded-full object-cover"
                />
              ) : (
                <span className="text-white text-sm font-medium">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              )}
            </div>
            <span className="flex-1 text-gray-500 dark:text-gray-400">Start a post...</span>
          </button>
        </div>
      )}
      {showCreatePost && <CreatePost onClose={() => setShowCreatePost(false)} />}
      {posts.map((post, index) => (
        <motion.div
          key={post.id}
          ref={(el) => { postRefs.current[post.id] = el; }}
          custom={index}
        >
          <PostCard post={post} />
        </motion.div>
      ))}
      
      {/* Infinite scroll trigger */}
      <div ref={loadMoreRef} className="py-4">
        {isFetchingNextPage && (
          <div className="theme-card shadow-sm p-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading more posts...</p>
          </div>
        )}
        
        {hasNextPage && !isFetchingNextPage && (
          <div className="text-center py-4">
            <button
              onClick={() => fetchNextPage()}
              className="px-6 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors"
            >
              Load More Posts
            </button>
          </div>
        )}
        
        {!hasNextPage && posts.length > 0 && (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            <p className="text-sm">You've reached the end of the feed!</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}